import React, { Fragment, useCallback } from 'react'
import styled from 'styled-components'
import { format } from 'date-fns'
import { useNavigate } from 'react-router-dom'
import { FiClock } from 'react-icons/fi'
import { useWindowSize } from 'customer/hooks/useWindowSize'
import { styleTheme } from '../../../../style/theme'
import { getPrimaryColor } from '../../../../style/utils'
import { useConfig } from '../../../../routes'
import { Availability } from '../../../../components/ui-elements/Icons/Availability'
import { Button } from '../../../../components/ui-elements/Button'
import { ImageComponent } from '../../../../components/ui-elements/Image'

import { FairEvent, pricingLabels } from '../../BridalFairs.types'

type Props = {
  fairEvent: FairEvent
  fairDate: Date
  bridalFairPageKey: string
}

const MOBILE_BREAKPOINT = 520

export const FairEventItem: React.FC<Props> = (props) => {
  const { primaryColor } = useConfig() as { primaryColor: string }
  const { width } = useWindowSize()
  const navigate = useNavigate()
  const onClickReserve = useCallback(
    ({
      fairEvent,
      bridalFairPageKey,
      fairDate,
    }: {
      fairEvent: FairEvent
      bridalFairPageKey: string
      fairDate: Date
    }): void => {
      navigate(
        `/customer/public/bridal_fair_pages/${bridalFairPageKey}/fairs/${
          fairEvent.bridalFairDetailKey
        }/fair_times/${format(fairDate, 'yyyy-MM-dd')}/participations/new`,
      )
    },
    [navigate],
  )
  const onClickDetail = useCallback(
    ({
      fairEvent,
      bridalFairPageKey,
      fairDate,
    }: {
      fairEvent: FairEvent
      bridalFairPageKey: string
      fairDate: Date
    }): void => {
      navigate(
        `/customer/public/bridal_fair_pages/${bridalFairPageKey}/fairs/${
          fairEvent.bridalFairDetailKey
        }?fair_date=${format(fairDate, 'yyyy-MM-dd')}`,
      )
    },
    [navigate],
  )
  return width <= MOBILE_BREAKPOINT ? (
    <WithMobile {...props} primaryColor={primaryColor} onClickReserve={onClickReserve} onClickDetail={onClickDetail} />
  ) : (
    <StyledComponent
      {...props}
      primaryColor={primaryColor}
      onClickReserve={onClickReserve}
      onClickDetail={onClickDetail}
    />
  )
}

const Component: React.FC<
  Props & {
    className?: string
    primaryColor: string
    onClickReserve: ({
      fairEvent,
      bridalFairPageKey,
      fairDate,
    }: {
      fairEvent: FairEvent
      bridalFairPageKey: string
      fairDate: Date
    }) => void
    onClickDetail: ({
      fairEvent,
      bridalFairPageKey,
      fairDate,
    }: {
      fairEvent: FairEvent
      bridalFairPageKey: string
      fairDate: Date
    }) => void
  }
> = ({ className, fairEvent, fairDate, primaryColor, bridalFairPageKey, onClickReserve, onClickDetail }) => {
  return (
    <div className={className}>
      {fairEvent.mainImageData && <Image fairEvent={fairEvent} />}
      <div className="event-info-wrapper">
        <h4>{fairEvent.name}</h4>
        <pre>{fairEvent.descriptionText}</pre>
        <div className="tags">
          <Tags fairEvent={fairEvent} />
        </div>
        <div className="event-info-items">
          <Minutes fairEvent={fairEvent} />
          <div>
            <PricingAndOffers fairEvent={fairEvent} />
          </div>
        </div>
        <div>
          <FairTimes fairEvent={fairEvent} />
        </div>
        <div>
          <Buttons
            fairEvent={fairEvent}
            bridalFairPageKey={bridalFairPageKey}
            fairDate={fairDate}
            primaryColor={primaryColor}
            onClickReserve={onClickReserve}
            onClickDetail={onClickDetail}
          />
        </div>
      </div>
    </div>
  )
}

const StyledMobile = styled.div`
  display: grid;
  gap: 8px;
  padding: 12px;
  margin: 0 16px;
  border: 1px solid ${styleTheme.colors.gray.baseLight};
  background-color: ${styleTheme.colors.white};
  > h2 {
    font-size: 14px;
  }
  > .wrapper {
    display: flex;
    gap: 12px;
  }
`
const WithMobile: React.FC<
  Props & {
    primaryColor: string
    onClickReserve: ({
      fairEvent,
      bridalFairPageKey,
      fairDate,
    }: {
      fairEvent: FairEvent
      bridalFairPageKey: string
      fairDate: Date
    }) => void
    onClickDetail: ({
      fairEvent,
      bridalFairPageKey,
      fairDate,
    }: {
      fairEvent: FairEvent
      bridalFairPageKey: string
      fairDate: Date
    }) => void
  }
> = ({ fairEvent, fairDate, primaryColor, bridalFairPageKey, onClickReserve, onClickDetail }) => {
  return (
    <StyledMobile
      onClick={() => {
        onClickDetail({
          fairEvent,
          bridalFairPageKey,
          fairDate,
        })
      }}
    >
      <h2>{fairEvent.name}</h2>
      <div>
        <Tags fairEvent={fairEvent} />
      </div>
      <div className="wrapper">
        <div>
          <Image fairEvent={fairEvent} />
        </div>
        <div>
          <Minutes fairEvent={fairEvent} />
          <PricingAndOffers fairEvent={fairEvent} />
        </div>
      </div>
      <div>
        <FairTimes fairEvent={fairEvent} />
      </div>
      <div>
        <Buttons
          fairEvent={fairEvent}
          bridalFairPageKey={bridalFairPageKey}
          fairDate={fairDate}
          primaryColor={primaryColor}
          onClickReserve={onClickReserve}
          onClickDetail={onClickDetail}
        />
      </div>
    </StyledMobile>
  )
}

const StyledImage = styled.div`
  @media (max-width: 520px) {
    position: relative;
    width: 100%;
  }

  > img {
    width: 328px;
    height: 328px;
    object-fit: cover;
    @media (max-width: 520px) {
      width: 152px;
      height: 100px;
    }
  }
`
const Image: React.FC<{ fairEvent: FairEvent }> = ({ fairEvent }) => {
  const { width } = useWindowSize()
  const aspectRatio = width <= MOBILE_BREAKPOINT ? 'threeToTwoImage' : 'oneToOneImage'
  if (!fairEvent.mainImageData) return null

  return (
    <StyledImage>
      <ImageComponent imageData={fairEvent.mainImageData} aspectRatio={aspectRatio} altText="フェアトップイメージ" />
    </StyledImage>
  )
}

const StyledTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 4px;
  font-size: 14px;
  @media (max-width: 520px) {
    font-size: 12px;
  }

  > .tag {
    background-color: ${styleTheme.colors.gray.light};
    padding: 4px 12px;
    border-radius: 4px;
  }
`
const Tags = ({ fairEvent }: { fairEvent: FairEvent }) => {
  return (
    <StyledTags>
      {Array.from(new Set(fairEvent.tags.concat(fairEvent.contents))).map((tag, index) => (
        <span key={index} className="tag">
          {`#${tag}`}
        </span>
      ))}
    </StyledTags>
  )
}

const StyledMinutes = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
  @media (max-width: 520px) {
    font-size: 14px;
  }
`
const Minutes: React.FC<{ fairEvent: FairEvent }> = ({ fairEvent }) => {
  return (
    <StyledMinutes>
      <div>
        <FiClock />
      </div>
      <span>{`所要時間 ${fairEvent.minutes}分`}</span>
    </StyledMinutes>
  )
}

const StyledPricingAndOffers = styled.div`
  display: flex;
  gap: 4px;

  > span {
    color: ${getPrimaryColor};
    font-size: 12px;
    border: 1px solid ${getPrimaryColor};
    padding: 2px 8px;
    border-radius: 2px;
  }

  > .pricing-tag {
    color: ${styleTheme.colors.black};
    border-color: ${styleTheme.colors.black};
  }
`
const PricingAndOffers = ({ fairEvent }: { fairEvent: FairEvent }) => {
  return (
    <StyledPricingAndOffers>
      <span className="pricing-tag">{pricingLabels[fairEvent.pricing]}</span>
      {fairEvent.withSpecialOffers.forSuccessCustomer && <span>成約特典</span>}
      {fairEvent.withSpecialOffers.forVisitor && <span>来館特典</span>}
    </StyledPricingAndOffers>
  )
}

const StyledFairTimes = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  font-size: 14px;
  @media (max-width: 520px) {
    font-size: 12px;
  }

  > span {
    display: flex;
  }
`
const FairTimes = ({ fairEvent }: { fairEvent: FairEvent }) => {
  return (
    <StyledFairTimes>
      {fairEvent.fairTimes
        .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())
        .map((time, index) => (
          <Fragment key={`${format(time.startAt, 'HH:mm')}.${index}`}>
            {index !== 0 && <span>|</span>}
            <span>
              <Availability availability={fairEvent.isNetReservable ? time.availability : 'none'} />
            </span>
            <span>{`${format(time.startAt, 'HH:mm')}~`}</span>
          </Fragment>
        ))}
    </StyledFairTimes>
  )
}

const StyledButtons = styled.div`
  display: flex;
  gap: 8px;
  @media (max-width: 520px) {
    gap: 4px;
  }

  > .detail {
    min-width: 40%;
  }

  > .reserve {
    min-width: 60%;
  }
`
const Buttons: React.FC<{
  fairEvent: FairEvent
  bridalFairPageKey: string
  fairDate: Date
  primaryColor: string
  onClickReserve: ({
    fairEvent,
    bridalFairPageKey,
    fairDate,
  }: {
    fairEvent: FairEvent
    bridalFairPageKey: string
    fairDate: Date
  }) => void
  onClickDetail: ({
    fairEvent,
    bridalFairPageKey,
    fairDate,
  }: {
    fairEvent: FairEvent
    bridalFairPageKey: string
    fairDate: Date
  }) => void
}> = ({ fairEvent, bridalFairPageKey, fairDate, primaryColor, onClickReserve, onClickDetail }) => {
  return (
    <StyledButtons>
      <div className="reserve">
        <Button
          type="button"
          disabled={!fairEvent.isNetReservable || fairEvent.fairTimes.every((time) => time.availability === 'none')}
          onClick={(e) => {
            e.stopPropagation()
            onClickReserve({
              fairEvent,
              bridalFairPageKey,
              fairDate,
            })
          }}
          color={primaryColor}
        >
          予約する
        </Button>
      </div>
      <div className="detail">
        <Button
          type="button"
          onClick={() =>
            onClickDetail({
              fairEvent,
              bridalFairPageKey,
              fairDate,
            })
          }
          color={primaryColor}
          invert
          className="show-link"
        >
          詳しく見る
        </Button>
      </div>
    </StyledButtons>
  )
}

const StyledComponent = styled(Component)`
  display: flex;
  padding: 4px;
  border: 1px solid ${styleTheme.colors.gray.baseLight};
  color: ${styleTheme.colors.black};
  background-color: ${styleTheme.colors.white};
  flex-wrap: wrap;
  min-height: 28px;
  @media (max-width: 520px) {
    flex-direction: column;
  }

  > .event-info-wrapper {
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    flex: 1;

    > h4,
    pre {
      margin: 0;
    }

    > h4 {
      font-size: 18px;
      font-weight: bold;
      line-height: 1.5;
    }

    > pre {
      font-size: 14px;
      line-height: 1.5;
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }

    > .event-info-items {
      line-height: 1.5;
      display: flex;
      flex-wrap: wrap-reverse;
      gap: 16px 24px;
    }
  }
`
